import { useCallback, useEffect, useState } from "react";

import { getFieldType } from "./helpers";
import type { DateRangeState } from "./types";

interface UseFilterDraftParams<C> {
  filters?: Record<string, any> | null;
  filtrosCriteria?: C | null;
  searchValue: string;
  drawerOpen: boolean;
  setFiltrosCriteria?: (filters: C) => void;
}

/**
 * Estado de rascunho do drawer de filtros (seleções, ranges de data e buscas por campo).
 * Sincroniza a partir dos critérios aplicados ao abrir o drawer e materializa o critério
 * final ao aplicar — mantendo o estado transiente isolado do componente principal.
 */
export function useFilterDraft<C extends Record<string, any>>({
  filters,
  filtrosCriteria,
  searchValue,
  drawerOpen,
  setFiltrosCriteria,
}: UseFilterDraftParams<C>) {
  const [selected, setSelected] = useState<Record<string, any[]>>({});
  const [dateRanges, setDateRanges] = useState<Record<string, DateRangeState>>({});
  const [searches, setSearches] = useState<Record<string, string>>({});

  // Sincroniza o rascunho com os critérios externos quando o drawer abre.
  useEffect(() => {
    if (!filters || !filtrosCriteria || !drawerOpen) return;

    const newSelected: Record<string, any[]> = {};
    const newDateRanges: Record<string, DateRangeState> = {};

    Object.keys(filters).forEach((key) => {
      const options = filters[key]?.options;
      const value = filtrosCriteria[key];
      if (value === undefined || value === null) return;

      const type = getFieldType(options);

      if (type === "boolean") {
        newSelected[key] = value === true || value === "true" ? [true] : [];
      } else if (type === "year") {
        newSelected[key] = value ? [String(value)] : [];
      } else if (type === "date") {
        newDateRanges[key] = { from: value.from, to: value.to };
      } else if (Array.isArray(value) && Array.isArray(options)) {
        newSelected[key] = options.filter((opt) => {
          const id = typeof opt === "object" && opt !== null ? opt.id : opt;
          return value.includes(id);
        });
      }
    });

    setSelected(newSelected);
    setDateRanges(newDateRanges);
  }, [drawerOpen, filters, filtrosCriteria]);

  const reset = useCallback(() => {
    setSelected({});
    setDateRanges({});
    setSearches({});
  }, []);

  /** Materializa o rascunho num objeto de critérios e devolve-o (sem efeitos colaterais externos). */
  const buildCriteria = useCallback((): C => {
    const criteria: Record<string, any> = { search: searchValue };

    Object.keys(selected).forEach((filtro) => {
      const fieldDef = filters?.[filtro]?.options;
      const values = selected[filtro] || [];
      const type = getFieldType(fieldDef);

      if (type === "boolean") {
        criteria[filtro] = values.length > 0;
      } else if (type === "year") {
        criteria[filtro] = values.length ? Number(values[0]) : null;
      } else {
        criteria[filtro] = values.map((item) => (typeof item === "object" ? item.id : item));
      }
    });

    Object.keys(dateRanges).forEach((key) => {
      const r = dateRanges[key];
      if (r && (r.from || r.to)) {
        criteria[key] = { from: r.from || null, to: r.to || null };
      }
    });

    return criteria as C;
  }, [selected, dateRanges, searchValue, filters]);

  const apply = useCallback(() => {
    if (!setFiltrosCriteria) return;
    setFiltrosCriteria(buildCriteria());
  }, [setFiltrosCriteria, buildCriteria]);

  const toggle = useCallback((group: string, option: any, booleanMode = false) => {
    setSelected((prev) => {
      const groupSelected = prev[group] || [];
      const isOptionObj = typeof option === "object" && option !== null;
      const exists = booleanMode
        ? groupSelected.length > 0
        : groupSelected.some((item) => (isOptionObj ? item.id === option.id : item === option));

      const newGroupSelected = exists
        ? booleanMode
          ? []
          : groupSelected.filter((item) => (isOptionObj ? item.id !== option.id : item !== option))
        : booleanMode
          ? [true]
          : [...groupSelected, option];

      return { ...prev, [group]: newGroupSelected };
    });
  }, []);

  const bulkToggle = useCallback((group: string, optionsToToggle: any[], selectAll: boolean) => {
    setSelected((prev) => ({
      ...prev,
      [group]: selectAll ? optionsToToggle : [],
    }));
  }, []);

  const changeSearch = useCallback((group: string, value: string) => {
    setSearches((prev) => ({ ...prev, [group]: value }));
  }, []);

  const changeDate = useCallback((group: string, side: "from" | "to", value: string) => {
    setDateRanges((prev) => ({
      ...prev,
      [group]: { ...prev[group], [side]: value },
    }));
  }, []);

  const changeYear = useCallback((group: string, year: string) => {
    setSelected((prev) => ({ ...prev, [group]: year ? [year] : [] }));
  }, []);

  return {
    selected,
    dateRanges,
    searches,
    reset,
    apply,
    toggle,
    bulkToggle,
    changeSearch,
    changeDate,
    changeYear,
  };
}
